import { useState, useRef, useEffect, Suspense, lazy } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, MessageSquare, Phone, PhoneOff, X, ChevronDown, ChevronRight, Mic } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useThemeColors } from '../context/ThemeContext';
import { useToast } from '../context/ToastContext';
import { dsv2, dsv2Light } from '../styles/designSystemV2';
import { createLiveSession, clasificarErrorVoz, VOICE_START_TIMEOUT_MS } from '../assistant/live';
import { registerOpen, setVoiceStatus, registerStartVoice } from '../assistant/assistantStore';
import { useClientPro } from '../hooks/useClientPro';
import { zLayer } from '../styles/zIndex';
import { VOZ_EN_VIVO_ACTIVA } from '../utils/featureFlags';

const AssistantPanel = lazy(() => import('./AssistantPanel'));

// Beta abierta: disponible para todos los clientes. Para premium: false + isPro.
const ASSISTANT_BETA_OPEN = true;

export default function AssistantFab() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { effectiveRole, userProfile } = useAuth();
  const { isDark } = useThemeColors();
  const T = isDark ? dsv2 : dsv2Light;

  const [open, setOpen] = useState(false);         // panel expandido
  const [mode, setMode] = useState(null);          // null (selector) | 'chat' | 'voice'
  const [chatActive, setChatActive] = useState(false);
  const [vStatus, setVStatus] = useState('idle');  // idle | connecting | live | error
  const [voiceTutorial, setVoiceTutorial] = useState(false);
  const [vLine, setVLine] = useState('');
  const [vLeft, setVLeft] = useState(null);
  const liveRef = useRef(null);
  const startVoiceRef = useRef(null);

  // El bottom nav abre el asistente y refleja el estado de la llamada (store compartido).
  useEffect(() => { registerOpen(() => setOpen(true)); }, []);
  // Sin voz en vivo el selector «escribir o hablar» no tiene nada que elegir: abre el chat directo.
  useEffect(() => {
    if (!VOZ_EN_VIVO_ACTIVA && open && mode === null) { setMode('chat'); setChatActive(true); }
  }, [open, mode]);
  useEffect(() => { setVoiceStatus(vStatus); }, [vStatus]);
  // El tour de bienvenida arranca la voz global vía el store (gesto síncrono → mic).
  useEffect(() => { registerStartVoice((opts) => startVoiceRef.current && startVoiceRef.current(opts)); }, []);
  // Antes del primer return: los hooks no pueden ir después de una salida temprana.
  const { isPro } = useClientPro(userProfile);

  if (effectiveRole !== 'client') return null;
  // Las tres vías de Pro (legacy, riel de Azul y regalo de fundador). Antes solo se
  // miraba la legacy: al cerrar la beta, los Pro de Azul y los fundadores habrían
  // quedado fuera del asistente que están pagando.
  if (!ASSISTANT_BETA_OPEN && !isPro) return null;

  const startVoice = async ({ tutorial = false, seedContext = '' } = {}) => {
    setOpen(true);
    setMode('voice');
    setVoiceTutorial(tutorial);
    setVLine(''); setVStatus('connecting');
    // La cuenta atrás de abajo se rige por ESTO y no por el tiempo: una llamada
    // que ya está en vivo no se corta por haber tardado en conectar.
    let conectado = false;
    let vencido = false;
    const ctrl = createLiveSession({
      voice: 'Charon',
      userProfile,
      tutorial,
      // Lo conversado en el chat o en el diagnóstico, para que la llamada no
      // arranque en blanco y el usuario no tenga que contarlo dos veces.
      seedContext,
      navigate: (to, opts) => { setOpen(false); navigate(to, opts); }, // navegar minimiza para ver la pantalla
      onStatus: (s) => { if (s === 'live') conectado = true; setVStatus(s); },
      onLine: setVLine,
      onRemaining: setVLeft,
    });
    liveRef.current = ctrl;
    const cerrarLimpio = () => { setOpen(false); setMode(null); setVoiceTutorial(false); }; // no volver al selector

    const arranque = ctrl.start();
    const reloj = setTimeout(() => {
      if (conectado) return;
      // Si el usuario ya colgó (hangUp deja `liveRef` en null) o arrancó otra
      // llamada, esta cuenta atrás dejó de ser suya: avisar aquí sería decirle
      // "no se pudo conectar" medio minuto después de que él mismo se fuera.
      if (liveRef.current !== ctrl) return;
      vencido = true;
      console.error('[asistente/voz] la llamada no llegó a conectar en', VOICE_START_TIMEOUT_MS, 'ms');
      try { ctrl.stop(); } catch (_) {}
      // Si el arranque termina DESPUÉS de habernos rendido (el usuario decide el
      // permiso tarde), hay que soltar el micrófono: sin esto queda abierto sin
      // llamada, con el indicador de grabación encendido y sin nada detrás.
      arranque.then(() => { try { ctrl.stop(); } catch (_) {} }, () => {});
      liveRef.current = null;
      showToast(t('assistant.voiceTimeout'), 'warning');
      cerrarLimpio();
    }, VOICE_START_TIMEOUT_MS);

    try {
      await arranque;
      clearTimeout(reloj);
    } catch (e) {
      clearTimeout(reloj);
      // El detalle crudo es para quien diagnostica, no para el cliente.
      console.error('[asistente/voz] no se pudo iniciar la llamada:', e?.name || 'Error', e?.message || e, e);
      if (vencido) return; // el reloj ya avisó; dos avisos por un fallo confunden
      showToast(t(clasificarErrorVoz(e)), 'warning');
      cerrarLimpio();
    }
  };

  // Mantén el ref apuntando al startVoice actual (para el arranque externo del tour).
  startVoiceRef.current = startVoice;

  const hangUp = () => {
    try { liveRef.current?.stop(); } catch (_) {}
    liveRef.current = null;
    setVStatus('idle'); setVLine(''); setVLeft(null);
  };

  const closeAll = () => {
    hangUp();
    setChatActive(false);
    setMode(null);
    setOpen(false);
  };

  const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  // ── Botón flotante / barra minimizada ──────────────────────────────────────
  return (
    <>
      {/* Entrada: el bottom nav abre el asistente (no hay FAB flotante). */}

      {/* Selector: ¿Escribir o Hablar? */}
      {open && mode === null && (
        <div style={{ position: 'fixed', inset: 0, zIndex: zLayer.assistant, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
          <div onClick={() => setOpen(false)} style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)' }} />
          <div style={{ position: 'relative', width: 'min(92vw, 360px)', margin: '0 auto', marginBottom: 'calc(env(safe-area-inset-bottom) + 78px)', maxHeight: 'calc(100dvh - 110px)', overflowY: 'auto', background: T.surface, borderRadius: 20, border: `1px solid ${T.outline}`, boxShadow: T.shadow.lg, padding: '16px 18px 18px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 30, height: 30, borderRadius: '50%', background: T.red, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Sparkles size={17} color="#fff" />
              </div>
              <div style={{ flex: 1, minWidth: 0, fontSize: 16, fontWeight: 800, color: T.text }}>{t('assistant.choosePrompt')}</div>
              <button onClick={() => setOpen(false)} aria-label={t('assistant.close')} style={{ background: 'none', border: 'none', color: T.textMuted, cursor: 'pointer', padding: 4, display: 'flex', flexShrink: 0 }}><X size={20} /></button>
            </div>
            <div style={{ fontSize: 12.5, color: T.textMuted, margin: '2px 0 16px 40px' }}>{t('assistant.chooseSubtitle', { defaultValue: 'Elige cómo prefieres usarlo' })}</div>

            <button onClick={() => { setMode('chat'); setChatActive(true); }} style={{
              width: '100%', marginBottom: 10, padding: 14, borderRadius: 14, border: `1px solid ${T.outline}`, background: T.surfaceHigh, color: T.text,
              cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 14, textAlign: 'left',
            }}>
              <div style={{ width: 44, height: 44, borderRadius: 12, background: T.surfaceLow, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <MessageSquare size={22} color={T.red} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 15, fontWeight: 700 }}>{t('assistant.chooseChat')}</div>
                <div style={{ fontSize: 12.5, color: T.textMuted, fontWeight: 400 }}>{t('assistant.chooseChatDesc', { defaultValue: 'Chatea por texto o nota de voz' })}</div>
              </div>
              <ChevronRight size={18} color={T.textMuted} style={{ flexShrink: 0 }} />
            </button>

            {VOZ_EN_VIVO_ACTIVA && (
            <button onClick={() => startVoice()} style={{
              width: '100%', padding: 14, borderRadius: 14, border: 'none', background: T.red, color: '#fff',
              cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 14, textAlign: 'left',
            }}>
              <div style={{ width: 44, height: 44, borderRadius: 12, background: 'rgba(255,255,255,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Phone size={22} color="#fff" />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 15, fontWeight: 800 }}>{t('assistant.chooseVoice')}</div>
                <div style={{ fontSize: 12.5, color: 'rgba(255,255,255,0.85)', fontWeight: 400 }}>{t('assistant.chooseVoiceDesc', { defaultValue: 'Habla con el asistente en vivo' })}</div>
              </div>
              <ChevronRight size={18} color="#fff" style={{ flexShrink: 0 }} />
            </button>
            )}
          </div>
        </div>
      )}

      {/* Chat (texto) */}
      {chatActive && mode === 'chat' && (
        <Suspense fallback={null}>
          <AssistantPanel visible={open} onMinimize={() => setOpen(false)} onClose={closeAll} />
        </Suspense>
      )}

      {/* Voz (llamada) expandida */}
      {open && mode === 'voice' && (
        <div style={{ position: 'fixed', inset: 0, zIndex: zLayer.assistant, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
          <div onClick={() => setOpen(false)} style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)' }} />
          <div style={{ position: 'relative', width: 'min(92vw, 360px)', margin: '0 auto', marginBottom: 'calc(env(safe-area-inset-bottom) + 78px)', height: 'min(56dvh, 460px)', maxHeight: 'calc(100dvh - 110px)', display: 'flex', flexDirection: 'column', background: T.surface, borderRadius: 20, border: `1px solid ${T.outline}`, boxShadow: T.shadow.lg, overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 16px', borderBottom: `1px solid ${T.outline}` }}>
              <div style={{ width: 32, height: 32, borderRadius: '50%', background: T.red, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Mic size={18} color="white" /></div>
              <div style={{ flex: 1, fontSize: 15, fontWeight: 700, color: T.text }}>{voiceTutorial ? t('welcomeTour.panelTitle') : t('assistant.voiceTitle')}</div>
              {vLeft != null && <span style={{ fontSize: 13, fontWeight: 700, color: vLeft <= 30 ? T.red : T.textMuted }}>{fmt(vLeft)}</span>}
              <button onClick={() => setOpen(false)} aria-label={t('assistant.minimize')} style={{ background: 'none', border: 'none', color: T.textMuted, cursor: 'pointer', padding: 4 }}><ChevronDown size={22} /></button>
              <button onClick={closeAll} aria-label={t('assistant.close')} style={{ background: 'none', border: 'none', color: T.textMuted, cursor: 'pointer', padding: 4 }}><X size={20} /></button>
            </div>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 18, padding: 20, textAlign: 'center' }}>
              <div style={{ width: 88, height: 88, borderRadius: '50%', background: vStatus === 'live' ? T.red : T.surfaceHigh, color: vStatus === 'live' ? 'white' : T.textMuted, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Mic size={40} />
              </div>
              <div style={{ fontSize: 15, color: T.text, fontWeight: 600 }}>
                {vStatus === 'connecting' ? t('assistant.connecting') : vStatus === 'live' ? t('assistant.voiceLive') : vStatus === 'error' ? t('assistant.errorGeneric') : ''}
              </div>
              <div style={{ fontSize: 14, color: T.textMuted, minHeight: 40, maxWidth: 320 }}>{vLine}</div>
            </div>
            <div style={{ padding: 16, borderTop: `1px solid ${T.outline}`, display: 'flex', justifyContent: 'center' }}>
              <button onClick={closeAll} style={{ padding: '14px 28px', borderRadius: 14, border: 'none', background: T.red, color: 'white', fontWeight: 700, fontSize: 15, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}>
                <PhoneOff size={18} /> {t('assistant.hangup')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
